import {
 Label
} from 'shared/components/ui/label';
import LongTextField from 'builders/destudio/primitives/LongTextField';
import {
 Input
} from 'shared/components/ui/input';
import {
 Badge
} from 'shared/components/ui/badge';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from 'shared/components/ui/select';
import {
 Button
} from 'shared/components/ui/button';
import {
 ArrowUp, ArrowDown
} from 'lucide-react';
import InputValidation from 'shared/components/InputValidation';
import {
    JsonOpcodeStep, FlowDefinition, StepKind,
    TypedInput, TypedOutput, AuditBlock, ResourceManifest, EngineCapabilities
} from 'builders/builderOpcode/jsonMode/types';
import {
 V2_STEP_TYPES
} from './V2OpCodeBuilderContext';
import V2StepDispatcher from './V2StepDispatcher';
import TypedInputsEditor from './components/TypedInputsEditor';
import TypedOutputsEditor from './components/TypedOutputsEditor';
import DefinitionsEditor from './components/DefinitionsEditor';
import FlowsEditor from './components/FlowsEditor';
import StepSettingsEditor from './components/StepSettingsEditor';
import MetadataPanel from './components/MetadataPanel';
import type {
 SectionId
} from './V2NavigationPanel';

const V2DetailPanel = ({
    selectedSection,
    // Config props
    opCodeName,
    setOpCodeName,
    opCodeDescription,
    setOpCodeDescription,
    inputs,
    setInputs,
    defaultStepTimeoutMs,
    setDefaultStepTimeoutMs,
    // Steps props
    steps,
    onStepKindChange,
    onStepChange,
    onMoveStep,
    // Definitions & Flows props
    definitions,
    setDefinitions,
    flows,
    setFlows,
    // Outputs props
    outputs,
    setOutputs,
    // Metadata (read-only)
    opcodeId,
    schemaVersion,
    audit,
    resourcesUsed,
    engineCapabilities,
}: {
    selectedSection: SectionId;
    opCodeName: string;
    setOpCodeName: (name: string) => void;
    opCodeDescription: string;
    setOpCodeDescription: (desc: string) => void;
    inputs: TypedInput[];
    setInputs: (inputs: TypedInput[]) => void;
    defaultStepTimeoutMs: number | undefined;
    setDefaultStepTimeoutMs: (ms: number | undefined) => void;
    steps: JsonOpcodeStep[];
    onStepKindChange: (index: number, kind: StepKind) => void;
    onStepChange: (index: number, step: JsonOpcodeStep) => void;
    onMoveStep: (index: number, direction: 'up' | 'down') => void;
    definitions: JsonOpcodeStep[];
    setDefinitions: (defs: JsonOpcodeStep[]) => void;
    flows: FlowDefinition[];
    setFlows: (flows: FlowDefinition[]) => void;
    outputs: TypedOutput[];
    setOutputs: (outputs: TypedOutput[]) => void;
    opcodeId?: string;
    schemaVersion?: string;
    audit?: AuditBlock;
    resourcesUsed?: ResourceManifest;
    engineCapabilities?: EngineCapabilities;
}) => {
    // Config section
    if (selectedSection === 'config') {
        return (
            <div className="h-full overflow-y-auto">
                <div className="p-6 space-y-6 max-w-4xl">
                    <div>
                        <h2 className="text-lg font-semibold mb-4">Opcode Configuration</h2>
                    </div>

                    <div className="space-y-4">
                        <div className="w-full">
                            <Label>Title <span className="text-muted-foreground">(Required)</span></Label>
                            <InputValidation
                                value={opCodeName}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setOpCodeName(e.target.value)}
                                placeholder="Enter a unique title for this opcode"
                            />
                        </div>

                        <div>
                            <Label>Description <span className="text-muted-foreground">(Optional)</span></Label>
                            <LongTextField
                                label="Description"
                                value={opCodeDescription}
                                onChange={setOpCodeDescription}
                                placeholder="Enter a description for this opcode"
                                monospace={false}
                            />
                        </div>

                        <div>
                            <Label>
                                Default Step Timeout (ms) <span className="text-muted-foreground">(Optional)</span>
                            </Label>
                            <Input
                                type="number"
                                min={0}
                                value={typeof defaultStepTimeoutMs === 'number' ? defaultStepTimeoutMs : ''}
                                onChange={(e) => {
                                    const v = e.target.value.trim();
                                    setDefaultStepTimeoutMs(v === '' ? undefined : Math.max(0, parseInt(v, 10) || 0));
                                }}
                                placeholder="Applied to every step that doesn't override timeout_ms (e.g. 30000)"
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                                Cascade source for per-step <code>timeout_ms</code>. The engine enforces it via <code>asyncio.wait_for</code>.
                            </p>
                        </div>

                        <TypedInputsEditor
                            inputs={inputs}
                            onChange={setInputs}
                        />
                    </div>
                </div>
            </div>
        );
    }

    // Metadata section (informational, read-only)
    if (selectedSection === 'metadata') {
        return (
            <div className="h-full overflow-y-auto">
                <div className="p-6">
                    <MetadataPanel
                        opcodeId={opcodeId}
                        schemaVersion={schemaVersion}
                        audit={audit}
                        resourcesUsed={resourcesUsed}
                        engineCapabilities={engineCapabilities}
                    />
                </div>
            </div>
        );
    }

    // Step section
    if (selectedSection.startsWith('step-')) {
        const stepIndex = parseInt(selectedSection.replace('step-', ''), 10);
        const step = steps[stepIndex];

        if (!step) {
            return (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                    Step not found
                </div>
            );
        }

        const inputNames = inputs.map(i => i.name).filter(Boolean);
        const allStepIds = steps.map(s => s.id).filter(Boolean);

        return (
            <div className="h-full overflow-y-auto">
                <div className="p-6 space-y-4 max-w-4xl">
                    {/* Step header */}
                    <div className="flex items-center gap-3">
                        <h2 className="text-lg font-semibold">
                            Step {stepIndex + 1}
                        </h2>
                        <Badge variant="outline" className="font-mono text-xs">
                            {step.id}
                        </Badge>
                        <div className="flex-1" />
                        <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            disabled={stepIndex === 0}
                            onClick={() => onMoveStep(stepIndex, 'up')}
                            title="Move up"
                        >
                            <ArrowUp className="h-4 w-4" />
                        </Button>
                        <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            disabled={stepIndex === steps.length - 1}
                            onClick={() => onMoveStep(stepIndex, 'down')}
                            title="Move down"
                        >
                            <ArrowDown className="h-4 w-4" />
                        </Button>
                    </div>

                    {/* Step type selector */}
                    <div>
                        <Label>Step Type</Label>
                        <Select
                            value={step.kind}
                            onValueChange={(val) => onStepKindChange(stepIndex, val as StepKind)}
                        >
                            <SelectTrigger className="w-[250px]">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {V2_STEP_TYPES.map((t) => (
                                    <SelectItem key={t.name} value={t.name}>{t.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Step configuration (dispatched) */}
                    <V2StepDispatcher
                        step={step}
                        onStepChange={(updated) => onStepChange(stepIndex, updated)}
                        definitions={definitions}
                        flows={flows}
                        oelContext={{
                            inputs: inputNames,
                            previousStepIds: steps.slice(0, stepIndex).map(s => s.id),
                            loopVariables: step.for ? [step.for.item] : undefined,
                        }}
                    />

                    {/* V2+ Step settings */}
                    <StepSettingsEditor
                        step={step}
                        allStepIds={allStepIds}
                        onChange={(patch) => onStepChange(stepIndex, {
 ...step,
...patch
} as JsonOpcodeStep)}
                    />
                </div>
            </div>
        );
    }

    // Definitions section
    if (selectedSection === 'definitions') {
        const inputNames = inputs.map(i => i.name).filter(Boolean);
        return (
            <div className="h-full overflow-y-auto">
                <div className="p-6 space-y-4 max-w-4xl">
                    <h2 className="text-lg font-semibold">Definitions</h2>
                    <p className="text-sm text-muted-foreground">
                        Define reusable step templates that can be referenced by Choose steps.
                    </p>
                    <DefinitionsEditor
                        definitions={definitions}
                        onChange={setDefinitions}
                        inputs={inputNames}
                        steps={steps}
                    />
                </div>
            </div>
        );
    }

    // Flows section
    if (selectedSection === 'flows') {
        const inputNames = inputs.map(i => i.name).filter(Boolean);
        return (
            <div className="h-full overflow-y-auto">
                <div className="p-6 space-y-4 max-w-4xl">
                    <h2 className="text-lg font-semibold">Flows</h2>
                    <p className="text-sm text-muted-foreground">
                        Define named sub-workflows for Subflow and Choose steps.
                    </p>
                    <FlowsEditor
                        flows={flows}
                        definitions={definitions}
                        onChange={setFlows}
                        inputs={inputNames}
                    />
                </div>
            </div>
        );
    }

    // Output section (V2+ TypedOutput[])
    if (selectedSection === 'output') {
        return (
            <div className="h-full overflow-y-auto">
                <div className="p-6 space-y-4 max-w-4xl">
                    <h2 className="text-lg font-semibold">Outputs</h2>
                    <p className="text-sm text-muted-foreground">
                        Map step outputs to the opcode's typed output variables.
                    </p>
                    <TypedOutputsEditor
                        outputs={outputs}
                        onChange={setOutputs}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="flex items-center justify-center h-full text-muted-foreground">
            Select a section from the sidebar
        </div>
    );
};

export default V2DetailPanel;
